// Typed wrappers around Tauri's `invoke` for each Rust command.

import { invoke as tauriInvoke } from "@tauri-apps/api/core";
import type {
  CaptureState,
  FinalGuess,
  FirstRunStatus,
  GameMode,
} from "../types/contract";

const isTauri = typeof window !== "undefined" && window.hasOwnProperty("__TAURI_IPC__");

// Safe invoke wrapper for when running on a mobile browser
const invoke = <T>(cmd: string, args?: Record<string, unknown>): Promise<T> => {
  if (!isTauri) {
    console.warn(`[Mock Invoke] ${cmd}`, args);
    // Return mock values so the UI doesn't crash on the phone
    if (cmd === "first_run_status") {
      return Promise.resolve({ openrouter_configured: true, r2_configured: true, geonames_configured: true } as unknown as T);
    }
    if (cmd === "get_settings") {
      return Promise.resolve({ auto_detect_enabled: false, auto_fire_on_new_round: false, auto_truth_from_score_screen: false, is_nmpz: false, disabled_pipelines: [] } as unknown as T);
    }
    return Promise.resolve({} as T);
  }
  return tauriInvoke<T>(cmd, args);
};

export const getCaptureState = () =>
  invoke<CaptureState>("get_capture_state");

export const setFinalGuess = (guess: FinalGuess) =>
  invoke<void>("set_final_guess", { guess });

export type CopyOutcome =
  | { kind: "copied" }
  | { kind: "no_guess" }
  | { kind: "blocked_foreground_geoguessr" }
  | { kind: "blocked_ambiguous_browser" };

export const copyFinal = () => invoke<CopyOutcome>("copy_final");

export const setOpacity = (opacity: number) =>
  invoke<void>("set_opacity", { opacity });

export const firstRunStatus = () =>
  invoke<FirstRunStatus>("first_run_status");

export interface CredentialsInput {
  openrouter_api_key?: string | null;
  r2_access_key_id?: string | null;
  r2_secret_access_key?: string | null;
  r2_bucket?: string | null;
  r2_account_id?: string | null;
  r2_public_url_template?: string | null;
  geonames_username?: string | null;
}

/// Mirrors `commands::ErrorCategory`. Wizard maps each one to a
/// distinct hint about what to retry.
export type ErrorCategory =
  | "invalid_input"
  | "auth"
  | "network"
  | "internal";

/// Mirrors `commands::CommandError`. `user_message` is meant for
/// rendering directly; `detail` is dev-facing and goes behind a
/// "Show details" toggle.
export interface CommandError {
  category: ErrorCategory;
  user_message: string;
  detail?: string | null;
}

export const firstRunSaveCredentials = (creds: CredentialsInput) =>
  invoke<FirstRunStatus>("first_run_save_credentials", { creds });

export const getGameMode = () => invoke<GameMode>("get_game_mode");

export const setGameMode = (mode: GameMode) =>
  invoke<void>("set_game_mode", { mode });

export interface TruthInput {
  roundId: string;
  lat?: number | null;
  lng?: number | null;
  country?: string | null;
}

export const recordTruth = (t: TruthInput) =>
  invoke<void>("record_truth", {
    roundId: t.roundId,
    lat: t.lat ?? null,
    lng: t.lng ?? null,
    country: t.country ?? null,
  });

/// Mirrors atlas_storage::GuessRecord. Pipeline is a free-form
/// string (not the PipelineId union) because the DB also stores
/// the synthetic "final" tag for the synthesised guess row.
export interface GuessRecord {
  round_id: string;
  pipeline: string;
  source: string | null;
  lat: number | null;
  lng: number | null;
  confidence: number | null;
  region: string | null;
  reasoning: string | null;
  latency_ms: number | null;
  status: "complete" | "failed" | "cancelled" | "skipped";
  error_category: string | null;
  error_message: string | null;
}

export interface StoredRound {
  id: string;
  captured_at_ms: number;
  game_mode: string;
  truth_lat: number | null;
  truth_lng: number | null;
  truth_country: string | null;
  thumbnail_path: string | null;
  guesses: GuessRecord[];
}

export const recentRounds = (limit: number) =>
  invoke<StoredRound[]>("recent_rounds", { limit });

export interface SettingsSnapshot {
  auto_detect_enabled: boolean;
  auto_fire_on_new_round: boolean;
  auto_truth_from_score_screen: boolean;
  is_nmpz: boolean;
  disabled_pipelines: string[];
}

export const getSettings = () => invoke<SettingsSnapshot>("get_settings");

export const setSettings = (settings: SettingsSnapshot) =>
  invoke<void>("set_settings", { settings });

/// Tauri's `invoke` rejects with whatever value the Rust side returns
/// in `Err(...)`. For commands that return `Result<T, CommandError>`,
/// the rejection is a `CommandError` (untyped at the JS layer); call
/// sites should narrow with this guard before reaching for fields.
export function isCommandError(e: unknown): e is CommandError {
  return (
    typeof e === "object" &&
    e !== null &&
    "category" in e &&
    "user_message" in e
  );
}
